import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { LuPhone, LuArrowRight, LuGlobe } from "react-icons/lu";
import AgenticAiHero from "../assets/img/AgenticAiHero.png";
import "../assets/css/AgenticAi.css";

const AgenticAI = () => {
  const [, setShow] = useState(false);
  const capabilities = [
    {
      icon: <LuPhone />,
      title: "Inbound",
      points: [
        "Understands intent from the first sentence, no menu-diving, no 'press 1 for...'",
        "Completes real transactions: account updates, order status, troubleshooting, booking changes",
        "Escalates with full context the moment a request needs a human; no repeating yourself",
      ],
    },
    {
      icon: <LuArrowRight />,
      title: "Outbound",
      points: [
        "Automated campaigns for reminders, renewals, surveys, and win-back",
        "Dynamically adjusts tone and offer based on real-time customer response",
        "Every touch logged and scored, so your team knows what worked",
      ],
    },
    {
      icon: <LuGlobe />,
      title: "Omni-Channel",
      points: [
        "Voice, chat, email, SMS, WhatsApp, and social — one agent, every surface",
        "Context carries across channels — no starting over on a new one",
        "Consistent tone and policy enforcement everywhere",
      ],
    },
  ];

  const guardrails = [
    {
      number: "01",
      title: "Defined escalation paths",
      text: "The agent knows exactly what it's allowed to resolve alone, and hands off the rest with full context, not a cold transfer.",
    },
    {
      number: "02",
      title: "Human-in-the-loop oversight",
      text: "For anything involving money, compliance, or a frustrated customer.",
    },
    {
      number: "03",
      title: "Full conversation logging and analytics",
      text: "So every automated interaction is auditable after the fact.",
    },
  ];

  const benefits = [
    {
      title: "Intelligent Problem Solving",
      text: "Analyzes complex situations, reasons through challenges, and takes action to achieve desired outcomes.",
    },
    {
      title: "Integrates and doesn't replace",
      text: "Works with your existing contact center stack, CRM, ticketing, and telephony rather than requiring you to rip and replace it.",
    },
    {
      title: "Improves with use",
      text: "Every escalation is a learning signal, so the agent gets measurably better at handling that category of request over time. It does not stay static after deployment.",
    },
  ];

  return (
    <div id="agentic-ai">
      <Navbar />

      {/* HERO */}
      <section className="agentic-hero">
        <div className="container">
          <div className="agentic-hero-content">
            <div>
              <span className="section-tag">AGENTIC AI</span>

              <h1>
                AI that doesn't just talk.
                <br />
                It finishes the job.
              </h1>

              <p>
                Outcess Agentic AI handles inbound requests, runs outbound
                campaigns, and works across every channel your customers use,
                resolving what it can, and escalating what it can't, with a real
                person always one step away.
              </p>

              <button className="ccsnl-btn" onClick={() => setShow(true)}>
                Book a Demo
              </button>
            </div>

            <div className="hero-image">
              <img src={AgenticAiHero} alt="Agentic Ai Hero" />
            </div>
          </div>
        </div>
      </section>

      {/* DARK SECTION */}
      <section className="agentic-highlight">
        <div className="container">
          <h2>AI That Finishes the Job, Not Just Starts the Conversation</h2>

          <p>
            A chatbot that answers a question is table stakes. An agent that
            checks a balance, reschedules a delivery, updates an account, or
            closes a sale — inside a single conversation, without a human
            needing to step in unless something genuinely requires one — is a
            different category of tool. That's the difference between automated
            and agentic.
          </p>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="agentic-capabilities-section">
        <div className="container agentic-capabilities-content ">
          <div className="agentic-capabilities-left">
            <span className="section-tag">
              INBOUND · OUTBOUND · OMNI-CHANNEL
            </span>

            <h2>One Agent. Every Direction.</h2>

            <p>
              The same underlying agent handles requests coming in, campaigns
              going out, and every channel in between.
            </p>
          </div>

          <div className="agentic-capabilities-cards">
            {capabilities.map((item, index) => (
              <div key={index} className="agentic-capability-card">
                <div className="agentic-card-icon">{item.icon}</div>

                <h4>{item.title}</h4>

                <ul>
                  {item.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-it-works">
        <div className="container">
          <span className="section-tag">HOW IT WORKS</span>

          <h2>
            Built With Guardrails,
            <br />
            Not Just Guesses
          </h2>

          <div className="steps-grid">
            {guardrails.map((item, index) => (
              <div key={index} className="step-card">
                <span>{item.number}</span>
                <div className="card-info">
                  <h4>{item.title}</h4>
                  <p>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="benefits-section">
        <div className="container">
          <span className="section-tag">WHY AGENTIC</span>

          <h2>
            The Difference Shows Up in the
            <br />
            Details
          </h2>

          <p className="benefits-intro">
            Agentic AI combines reasoning, action, and human oversight so each
            customer request reaches a useful resolution.
          </p>

          <div className="benefits-grid">
            {benefits.map((item, index) => (
              <div key={index} className="agentic-benefit-card">
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="agentic-cta">
        <div className="container">
          <h2>Let AI handle what it can. Let your team handle what matters.</h2>

          <p>
            Book a demo and see how Agentic AI fits into your inbound, outbound,
            and omni-channel operations.
          </p>

          <button className="ccsnl-btn" onClick={() => setShow(true)}>
            Book a Demo
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgenticAI;
